import { useState } from 'react'
import { useLocation } from 'react-router-dom'
import {
  Button,
  Spinner,
  Title2,
  Tooltip,
  makeStyles,
  tokens,
} from '@fluentui/react-components'
import {
  ArrowForward24Regular,
  Chat16Regular,
  DocumentAdd24Regular,
  PersonKey24Regular,
} from '@fluentui/react-icons'
import api from '../lib/api'
import { apiErrorMessage } from '../lib/apiError'
import { safeRedirect } from '../lib/safeRedirect'
import { TeamsChatFab } from '../components/TeamsChatFab'
import { TEAMS_CHAT_URL, MAINTAINER_NAME } from '../lib/support'

// Color tokens borrowed from the v1 landing page on main: dark navy
// canvas with diffuse gradient orbs. This is the "welcome surface"
// look — surfaces inside the app stay on Bayer navy + neutral chrome.
const NAVY_BG = '#0f172a'
const ORB_BLUE = 'rgba(59, 130, 246, 0.3)'
const ORB_PURPLE = 'rgba(139, 92, 246, 0.25)'

const useStyles = makeStyles({
  root: {
    minHeight: '100vh',
    display: 'flex',
    backgroundColor: NAVY_BG,
    position: 'relative',
    overflow: 'hidden',
    color: '#f8fafc',
  },
  orbLayer: {
    position: 'absolute',
    inset: 0,
    overflow: 'hidden',
    pointerEvents: 'none',
  },
  orb: {
    position: 'absolute',
    borderRadius: '50%',
    filter: 'blur(100px)',
  },
  orbA: {
    width: '500px',
    height: '500px',
    background: `radial-gradient(circle, ${ORB_BLUE} 0%, transparent 70%)`,
    top: '-15%',
    right: '-10%',
  },
  orbB: {
    width: '400px',
    height: '400px',
    background: `radial-gradient(circle, ${ORB_PURPLE} 0%, transparent 70%)`,
    bottom: '-10%',
    left: '-5%',
  },
  // Two-panel: branding on the left, sign-in card on the right.
  // Below 900px the left panel collapses; only the card remains.
  branding: {
    flex: '1 1 50%',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    padding: '64px',
    position: 'relative',
    zIndex: 1,
    '@media (max-width: 900px)': {
      display: 'none',
    },
  },
  brandingInner: {
    maxWidth: '480px',
  },
  logoRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    marginBottom: '48px',
  },
  logoIcon: {
    width: '56px',
    height: '56px',
    objectFit: 'contain',
    filter:
      'drop-shadow(0 0 1px rgba(255,255,255,0.22)) drop-shadow(0 2px 12px rgba(0,0,0,0.5))',
  },
  logoWordmark: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
  },
  logoTitle: {
    fontSize: '20px',
    fontWeight: 700,
    letterSpacing: '-0.02em',
    color: '#f8fafc',
    lineHeight: 1.2,
  },
  logoSubtitle: {
    fontSize: '11px',
    fontWeight: 500,
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
    color: 'rgba(148, 163, 184, 0.9)',
    lineHeight: 1.2,
  },
  hero: {
    fontSize: '36px',
    lineHeight: 1.15,
    fontWeight: 700,
    letterSpacing: '-0.02em',
    color: '#f8fafc',
    marginBottom: '32px',
  },
  bullets: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
    color: 'rgba(203, 213, 225, 0.9)',
    fontSize: '14px',
  },
  bullet: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '10px',
  },
  bulletDot: {
    width: '6px',
    height: '6px',
    borderRadius: '50%',
    background: 'linear-gradient(135deg, #3b82f6, #8b5cf6)',
    marginTop: '7px',
    flexShrink: 0,
  },
  // Sign-in panel.
  signIn: {
    flex: '1 1 50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '32px',
    position: 'relative',
    zIndex: 1,
  },
  card: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'stretch',
    gap: tokens.spacingVerticalL,
    padding: '40px',
    borderRadius: tokens.borderRadiusXLarge,
    backgroundColor: 'rgba(255, 255, 255, 0.98)',
    boxShadow: '0 20px 60px rgba(0, 0, 0, 0.4)',
    maxWidth: '420px',
    width: '100%',
    color: '#1e293b',
  },
  cardHeader: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '8px',
    marginBottom: '8px',
  },
  cardLogo: {
    width: '48px',
    height: '48px',
    objectFit: 'contain',
    marginBottom: '4px',
    // Only show on narrow viewports where the left branding panel is hidden.
    '@media (min-width: 901px)': {
      display: 'none',
    },
  },
  errorText: {
    fontSize: '12px',
    color: tokens.colorPaletteRedForeground1,
    textAlign: 'center',
  },
  footer: {
    textAlign: 'center',
    color: 'rgba(100, 116, 139, 0.7)',
    fontSize: '11px',
    letterSpacing: '0.04em',
    textTransform: 'uppercase',
    marginTop: '8px',
  },
  helpLink: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '6px',
    fontSize: '13px',
    fontWeight: 600,
    color: '#1f5bb5',
    textDecoration: 'none',
    padding: '6px',
  },
  // Secondary triage area (owner ask, 2026-09-13): the two front doors that
  // make sense BEFORE knowing the app — submitting a demand, and the DSAI
  // queue. Two small icon tiles (owner ask, 2026-09-22): the paragraphs they
  // replaced read like body text and pulled the eye off Sign in. The label
  // stays visible — a first-time visitor cannot read a bare icon — and the
  // explanation moved into the tooltip.
  quickDivider: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    color: '#94a3b8',
    fontSize: '11px',
    letterSpacing: '0.06em',
    textTransform: 'uppercase' as const,
    '::before': { content: '""', flexGrow: 1, height: '1px', background: '#e2e8f0' },
    '::after': { content: '""', flexGrow: 1, height: '1px', background: '#e2e8f0' },
  },
  quickRow: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '10px',
  },
  quickTile: {
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'center',
    gap: '6px',
    padding: '12px 8px',
    background: 'none',
    border: '1px solid #e2e8f0',
    borderRadius: '10px',
    cursor: 'pointer',
    fontFamily: 'inherit',
    fontSize: '13px',
    fontWeight: 600,
    color: '#1f5bb5',
    ':hover': { background: '#f1f5f9', border: '1px solid #cbd5e1' },
    ':focus-visible': { outline: '2px solid #1f5bb5', outlineOffset: '2px' },
    ':disabled': { opacity: 0.6, cursor: 'default' },
  },
})

export function Login() {
  const styles = useStyles()
  const location = useLocation()
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  const handleLogin = async (dest?: string) => {
    setLoading(true)
    setError('')
    try {
      // Where to land post-SSO: an explicit destination (the quick links
      // below) beats where ProtectedRoute bounced us from. Threaded to the
      // backend, which round-trips it through the OAuth `state` and appends
      // it to the callback as ?redirect=… so we land back there post-SSO.
      // ProtectedRoute passes it as router state; the 401 handler, which has
      // to reload the page, passes it as ?from=.
      const from =
        (location.state as { from?: string } | null)?.from ??
        new URLSearchParams(location.search).get('from') ??
        undefined
      const redirectUrl = safeRedirect(dest ?? from)
      const res = await api.get('/auth/azure/login', { params: { redirect_url: redirectUrl } })
      window.location.href = res.data.auth_url
    } catch (err: unknown) {
      setError(
        apiErrorMessage(
          err,
          'Azure SSO unavailable. Please try again or contact an admin.',
        ),
      )
      setLoading(false)
    }
  }

  return (
    <div className={styles.root}>
      <div className={styles.orbLayer} aria-hidden="true">
        <div className={`${styles.orb} ${styles.orbA}`} />
        <div className={`${styles.orb} ${styles.orbB}`} />
      </div>

      <div className={styles.branding}>
        <div className={styles.brandingInner}>
          <div className={styles.logoRow}>
            <img src="/dac-logo.svg" alt="" className={styles.logoIcon} />
            <div className={styles.logoWordmark}>
              <span className={styles.logoTitle}>DACcelerate</span>
              <span className={styles.logoSubtitle}>Data &amp; Analytics Council</span>
            </div>
          </div>

          <div className={styles.hero}>
            Match talent to the demands that need them.
          </div>

          <div className={styles.bullets}>
            <div className={styles.bullet}>
              <span className={styles.bulletDot} />
              <span>Browse open demands in every Focus Area.</span>
            </div>
            <div className={styles.bullet}>
              <span className={styles.bulletDot} />
              <span>Get invited for the skills on your profile.</span>
            </div>
            <div className={styles.bullet}>
              <span className={styles.bulletDot} />
              <span>Track applications, priorities and outcomes.</span>
            </div>
          </div>
        </div>
      </div>

      <div className={styles.signIn}>
        <div className={styles.card}>
          <div className={styles.cardHeader}>
            <img src="/dac-logo.svg" alt="" className={styles.cardLogo} />
            <Title2 as="h1" style={{ color: '#0f172a' }}>
              Sign in
            </Title2>
          </div>

          <Button
            appearance="primary"
            size="large"
            icon={loading ? <Spinner size="tiny" /> : <PersonKey24Regular />}
            onClick={() => handleLogin()}
            disabled={loading}
            style={{ width: '100%' }}
          >
            {loading ? 'Redirecting…' : 'Sign in with Azure AD'}
          </Button>

          {error && <div className={styles.errorText}>{error}</div>}

          <a
            className={styles.helpLink}
            href={TEAMS_CHAT_URL}
            target="_blank"
            rel="noopener noreferrer"
          >
            <Chat16Regular aria-hidden />
            Trouble signing in? Message {MAINTAINER_NAME} on Teams
          </a>

          <div className={styles.quickDivider}>or go straight to</div>

          <div className={styles.quickRow}>
            <Tooltip
              content="Describe what you need — the right council picks it up. No need to know this app."
              relationship="description"
            >
              <button
                type="button"
                className={styles.quickTile}
                onClick={() => handleLogin('/submit-demand')}
                disabled={loading}
                data-testid="login-quick-submit"
              >
                <DocumentAdd24Regular aria-hidden="true" />
                Submit a demand
              </button>
            </Tooltip>
            <Tooltip
              content="Requests we routed to DSAI (not DAC matters), with the reason each one was sent."
              relationship="description"
            >
              <button
                type="button"
                className={styles.quickTile}
                onClick={() => handleLogin('/dsai')}
                disabled={loading}
                data-testid="login-quick-dc"
              >
                <ArrowForward24Regular aria-hidden="true" />
                DSAI queue
              </button>
            </Tooltip>
          </div>

          <div className={styles.footer}>Internal tool · DAC TalentFlow</div>
        </div>
      </div>

      {/* Maintainer-contact FAB — same component AppLayout mounts inside the
        * app shell. Surfaced on Login too so SSO-failure cases can reach
        * the maintainer without first signing in. */}
      <TeamsChatFab />
    </div>
  )
}
